import React, {
    useEffect,
    useMemo,
    useState
} from 'react';
import { Link } from 'react-router-dom';
import './adminFaq.css';
import API_BASE_URL from '../../../config/api';

const categories = [
    '전체',
    'LED 전광판',
    '빔프로젝터',
    '예약·결제',
    '설치·운영'
];

const ITEMS_PER_PAGE = 10;

const AdminFaqList = () => {
    const [faqs, setFaqs] =
        useState([]);
    const [selectedCategory, setSelectedCategory] =
        useState('전체');
    const [searchKeyword, setSearchKeyword] =
        useState('');
    const [currentPage, setCurrentPage] =
        useState(1);
    const [selectedFaq, setSelectedFaq] =
        useState(null);
    const [isLoading, setIsLoading] =
        useState(true);
    const [isDetailLoading, setIsDetailLoading] =
        useState(false);
    const [error, setError] =
        useState('');
    const [detailError, setDetailError] =
        useState('');

    /*
     * ================================================
     * FAQ LIST
     * ================================================
     */

    useEffect(() => {
        const fetchFaqs = async () => {
            try {
                setIsLoading(true);
                setError('');
                const response = await fetch(
                    `${API_BASE_URL}/api/faqs/admin`,
                    {
                        method: 'GET',
                        credentials: 'include'
                    }
                );
                const result =
                    await response.json();
                if (!response.ok) {
                    throw new Error(
                        result.message ||
                        'FAQ를 불러오지 못했습니다.'
                    );
                }
                setFaqs(
                    result.faqs || []
                );
            } catch (error) {
                console.error(
                    'FAQ 조회 오류:',
                    error
                );
                setError(
                    error.message
                );
            } finally {
                setIsLoading(false);
            }
        };
        fetchFaqs();
    }, []);

    /*
     * ================================================
     * BODY SCROLL
     * ================================================
     */

    useEffect(() => {
        if (
            selectedFaq ||
            isDetailLoading ||
            detailError
        ) {
            document.body.style.overflow =
                'hidden';
        } else {
            document.body.style.overflow =
                '';
        }
        return () => {
            document.body.style.overflow =
                '';
        };
    }, [
        selectedFaq,
        isDetailLoading,
        detailError
    ]);

    /*
     * ================================================
     * FILTER
     * ================================================
     */

    const filteredFaqs =
        useMemo(() => {
            const keyword =
                searchKeyword
                    .trim()
                    .toLowerCase();
            return faqs.filter(
                (faq) => {
                    const matchesCategory =
                        selectedCategory === '전체' ||
                        faq.category ===
                        selectedCategory;
                    if (!matchesCategory) {
                        return false;
                    }
                    if (!keyword) {
                        return true;
                    }
                    const searchableText = [
                        faq.category,
                        faq.question,
                        faq.answer,
                        faq.created_at
                    ]
                        .filter(Boolean)
                        .join(' ')
                        .toLowerCase();
                    return searchableText.includes(
                        keyword
                    );
                }
            );
        }, [
            faqs,
            selectedCategory,
            searchKeyword
        ]);
    const handleCategoryChange = (category) => {
        setSelectedCategory(category);
        setCurrentPage(1);
    };
    const handleSearchChange = (event) => {
        setSearchKeyword(
            event.target.value
        );
        setCurrentPage(1);
    };
    const handleClearSearch = () => {
        setSearchKeyword('');
        setCurrentPage(1);
    };
    const totalPages = Math.max(
        1,
        Math.ceil(
            filteredFaqs.length /
            ITEMS_PER_PAGE
        )
    );
    const paginatedFaqs =
        useMemo(() => {
            const startIndex =
                (currentPage - 1) *
                ITEMS_PER_PAGE;
            const endIndex =
                startIndex +
                ITEMS_PER_PAGE;
            return filteredFaqs.slice(
                startIndex,
                endIndex
            );
        }, [
            filteredFaqs,
            currentPage
        ]);
    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(
                totalPages
            );
        }
    }, [
        currentPage,
        totalPages
    ]);
    const pageNumbers = useMemo(() => {
        if (totalPages <= 5) {
            return Array.from(
                {
                    length: totalPages
                },
                (_, index) =>
                    index + 1
            );
        }
        let startPage = Math.max(
            1,
            currentPage - 2
        );
        const endPage = Math.min(
            totalPages,
            startPage + 4
        );
        if (endPage - startPage < 4) {
            startPage = Math.max(
                1,
                endPage - 4
            );
        }
        return Array.from(
            {
                length:
                    endPage - startPage + 1
            },
            (_, index) =>
                startPage + index
        );
    }, [
        currentPage,
        totalPages
    ]);

    /*
     * ================================================
     * DATE
     * ================================================
     */

    const formatDate = (date) => {
        if (!date) {
            return '-';
        }
        return new Intl.DateTimeFormat(
            'ko-KR',
            {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            }
        ).format(
            new Date(date)
        );
    };

    /*
     * ================================================
     * DETAIL
     * ================================================
     */

    const handleView = async (
        faqId
    ) => {
        try {
            setIsDetailLoading(true);
            setDetailError('');
            setSelectedFaq(null);
            const response = await fetch(
                `${API_BASE_URL}/api/faqs/admin/${faqId}`,
                {
                    method: 'GET',
                    credentials: 'include'
                }
            );
            const result =
                await response.json();
            if (!response.ok) {
                throw new Error(
                    result.message ||
                    'FAQ를 불러오지 못했습니다.'
                );
            }
            setSelectedFaq(
                result.faq
            );
        } catch (error) {
            console.error(
                'FAQ 상세 조회 오류:',
                error
            );
            setDetailError(
                error.message
            );
        } finally {
            setIsDetailLoading(false);
        }
    };
    const handleCloseDetail = () => {
        setSelectedFaq(null);
        setDetailError('');
    };

    /*
     * ================================================
     * DELETE
     * ================================================
     */

    const handleDelete = async (
        faqId,
        question
    ) => {
        const confirmed =
            window.confirm(
                `"${question}" FAQ를 삭제하시겠습니까?\n삭제된 내용은 복구할 수 없습니다.`
            );
        if (!confirmed) {
            return;
        }
        try {
            const response = await fetch(
                `${API_BASE_URL}/api/faqs/admin/${faqId}`,
                {
                    method: 'DELETE',
                    credentials: 'include'
                }
            );
            const result =
                await response.json();
            if (!response.ok) {
                throw new Error(
                    result.message ||
                    'FAQ 삭제에 실패했습니다.'
                );
            }
            setFaqs((prev) =>
                prev.filter(
                    (faq) =>
                        faq.faq_id !==
                        faqId
                )
            );
            if (
                selectedFaq?.faq_id ===
                faqId
            ) {
                handleCloseDetail();
            }
            alert(
                'FAQ가 삭제되었습니다.'
            );
        } catch (error) {
            console.error(
                'FAQ 삭제 오류:',
                error
            );
            alert(
                error.message
            );
        }
    };
    return (
        <div className="admin-faq">
            {/* HEADER */}
            <div className="admin-faq-header">
                <div>
                    <p>
                        FAQ
                    </p>
                    <h1>
                        FAQ 관리
                    </h1>
                    <span>
                        홈페이지에 노출되는 자주 묻는 질문을 관리합니다.
                    </span>
                </div>
                <Link
                    to="/admin/faqs/new"
                    className="admin-faq-add"
                >
                    <i className="mdi mdi-plus"></i>
                    FAQ 등록
                </Link>
            </div>
            {/* CONTENT */}
            <div className="admin-faq-content">
                {/* CATEGORY + SEARCH */}
                <div className="admin-faq-toolbar">
                    <div className="admin-faq-tabs">
                        {categories.map(
                            (category) => (
                                <button
                                    type="button"
                                    key={category}
                                    className={
                                        selectedCategory ===
                                        category
                                            ? 'active'
                                            : ''
                                    }
                                    onClick={() =>
                                        handleCategoryChange(
                                            category
                                        )
                                    }
                                >
                                    {category}
                                </button>
                            )
                        )}
                    </div>
                    <div className="admin-faq-search-area">
                        <span className="admin-faq-result-count">
                            {filteredFaqs.length}건
                        </span>
                        <div className="admin-faq-search">
                            <i className="mdi mdi-magnify"></i>
                            <input
                                type="text"
                                value={searchKeyword}
                                onChange={handleSearchChange}
                                placeholder="질문, 답변, 카테고리 검색"
                                aria-label="FAQ 검색"
                            />
                            {searchKeyword && (
                                <button
                                    type="button"
                                    className="admin-faq-search-clear"
                                    onClick={handleClearSearch}
                                    aria-label="검색어 지우기"
                                >
                                    <i className="mdi mdi-close"></i>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                {isLoading ? (
                    <div className="admin-faq-empty">
                        <i className="mdi mdi-loading mdi-spin"></i>
                        <strong>
                            FAQ를 불러오는 중입니다.
                        </strong>
                    </div>
                ) : error ? (
                    <div className="admin-faq-empty">
                        <i className="mdi mdi-alert-circle-outline"></i>
                        <strong>
                            FAQ를 불러오지 못했습니다.
                        </strong>
                        <p>
                            {error}
                        </p>
                    </div>
                ) : filteredFaqs.length === 0 ? (
                    <div className="admin-faq-empty">
                        <i className="mdi mdi-help-circle-outline"></i>
                        <strong>
                            등록된 FAQ가 없습니다.
                        </strong>
                        <p>
                            새로운 FAQ를 등록해주세요.
                        </p>
                    </div>
                ) : (
                    <div className="admin-faq-results">
                        <div className="admin-faq-table-wrap">
                        <table className="admin-faq-table">
                            <thead>
                                <tr>
                                    <th>
                                        번호
                                    </th>
                                    <th>
                                        카테고리
                                    </th>
                                    <th>
                                        질문
                                    </th>
                                    <th>
                                        정렬순서
                                    </th>
                                    <th>
                                        공개상태
                                    </th>
                                    <th>
                                        등록일
                                    </th>
                                    <th>
                                        관리
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedFaqs.map(
                                    (faq) => (
                                        <tr
                                            key={
                                                faq.faq_id
                                            }
                                        >
                                            <td>
                                                {
                                                    faq.faq_id
                                                }
                                            </td>
                                            <td>
                                                <span className="admin-faq-category">
                                                    {
                                                        faq.category
                                                    }
                                                </span>
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="admin-faq-question"
                                                    onClick={() =>
                                                        handleView(
                                                            faq.faq_id
                                                        )
                                                    }
                                                >
                                                    {
                                                        faq.question
                                                    }
                                                </button>
                                            </td>
                                            <td>
                                                {
                                                    faq.sort_order
                                                }
                                            </td>
                                            <td>
                                                <span
                                                    className={
                                                        faq.is_active
                                                            ? 'admin-faq-status active'
                                                            : 'admin-faq-status inactive'
                                                    }
                                                >
                                                    {faq.is_active
                                                        ? '공개'
                                                        : '비공개'
                                                    }
                                                </span>
                                            </td>
                                            <td>
                                                {formatDate(
                                                    faq.created_at
                                                )}
                                            </td>
                                            <td>
                                                <div className="admin-faq-actions">
                                                    <button
                                                        type="button"
                                                        className="admin-faq-view"
                                                        onClick={() =>
                                                            handleView(
                                                                faq.faq_id
                                                            )
                                                        }
                                                    >
                                                        보기
                                                    </button>
                                                    <Link
                                                        to={`/admin/faqs/${faq.faq_id}/edit`}
                                                        className="admin-faq-edit"
                                                    >
                                                        수정
                                                    </Link>
                                                    <button
                                                        type="button"
                                                        className="admin-faq-delete"
                                                        onClick={() =>
                                                            handleDelete(
                                                                faq.faq_id,
                                                                faq.question
                                                            )
                                                        }
                                                    >
                                                        삭제
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>
                        {totalPages > 1 && (
                            <div className="admin-faq-pagination">
                                <button
                                    type="button"
                                    className="admin-faq-pagination-arrow"
                                    onClick={() =>
                                        setCurrentPage(
                                            (prev) =>
                                                Math.max(
                                                    prev - 1,
                                                    1
                                                )
                                        )
                                    }
                                    disabled={currentPage === 1}
                                    aria-label="이전 페이지"
                                >
                                    <i className="mdi mdi-chevron-left"></i>
                                </button>
                                <div className="admin-faq-pagination-pages">
                                    {pageNumbers.map(
                                        (page) => (
                                            <button
                                                key={page}
                                                type="button"
                                                className={
                                                    currentPage === page
                                                        ? 'active'
                                                        : ''
                                                }
                                                onClick={() =>
                                                    setCurrentPage(
                                                        page
                                                    )
                                                }
                                            >
                                                {page}
                                            </button>
                                        )
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="admin-faq-pagination-arrow"
                                    onClick={() =>
                                        setCurrentPage(
                                            (prev) =>
                                                Math.min(
                                                    prev + 1,
                                                    totalPages
                                                )
                                        )
                                    }
                                    disabled={
                                        currentPage ===
                                        totalPages
                                    }
                                    aria-label="다음 페이지"
                                >
                                    <i className="mdi mdi-chevron-right"></i>
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
            {/* DETAIL LOADING */}
            {isDetailLoading && (
                <div className="admin-faq-modal-overlay">
                    <div className="admin-faq-modal-loading">
                        <i className="mdi mdi-loading mdi-spin"></i>
                        <strong>
                            FAQ를 불러오는 중입니다.
                        </strong>
                    </div>
                </div>
            )}
            {/* DETAIL ERROR */}
            {!isDetailLoading &&
                detailError && (
                    <div
                        className="admin-faq-modal-overlay"
                        onClick={handleCloseDetail}
                    >
                        <div
                            className="admin-faq-modal-loading"
                            onClick={(event) =>
                                event.stopPropagation()
                            }
                        >
                            <i className="mdi mdi-alert-circle-outline"></i>
                            <strong>
                                FAQ를 불러오지 못했습니다.
                            </strong>
                            <p>
                                {detailError}
                            </p>
                            <button
                                type="button"
                                onClick={handleCloseDetail}
                            >
                                닫기
                            </button>
                        </div>
                    </div>
                )}
            {/* DETAIL MODAL */}
            {!isDetailLoading &&
                selectedFaq && (
                    <div
                        className="admin-faq-modal-overlay"
                        onClick={handleCloseDetail}
                    >
                        <div
                            className="admin-faq-modal"
                            onClick={(event) =>
                                event.stopPropagation()
                            }
                        >
                            <div className="admin-faq-modal-header">
                                <div>
                                    <p>
                                        FAQ DETAIL
                                    </p>
                                    <h2>
                                        {
                                            selectedFaq.question
                                        }
                                    </h2>
                                </div>
                                <button
                                    type="button"
                                    className="admin-faq-modal-close"
                                    onClick={
                                        handleCloseDetail
                                    }
                                    aria-label="닫기"
                                >
                                    <i className="mdi mdi-close"></i>
                                </button>
                            </div>
                            <div className="admin-faq-modal-body">
                                <div className="admin-faq-detail-info">
                                    <DetailRow
                                        label="카테고리"
                                        value={
                                            selectedFaq.category
                                        }
                                    />
                                    <DetailRow
                                        label="정렬순서"
                                        value={
                                            selectedFaq.sort_order
                                        }
                                    />
                                    <DetailRow
                                        label="공개상태"
                                        value={
                                            selectedFaq.is_active
                                                ? '공개'
                                                : '비공개'
                                        }
                                    />
                                    <DetailRow
                                        label="등록일"
                                        value={
                                            formatDate(
                                                selectedFaq.created_at
                                            )
                                        }
                                    />
                                </div>
                                <div className="admin-faq-detail-answer">
                                    <strong>
                                        답변
                                    </strong>
                                    <div>
                                        {
                                            selectedFaq.answer
                                        }
                                    </div>
                                </div>
                            </div>
                            <div className="admin-faq-modal-footer">
                                {selectedFaq.is_active && (
                                    <Link
                                        to="/support/faq"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="admin-faq-public"
                                    >
                                        실제 페이지 보기
                                        <i className="mdi mdi-open-in-new"></i>
                                    </Link>
                                )}
                                <Link
                                    to={`/admin/faqs/${selectedFaq.faq_id}/edit`}
                                    className="admin-faq-modal-edit"
                                >
                                    수정하기
                                </Link>
                                <button
                                    type="button"
                                    className="admin-faq-modal-cancel"
                                    onClick={
                                        handleCloseDetail
                                    }
                                >
                                    닫기
                                </button>
                            </div>
                        </div>
                    </div>
                )}
        </div>
    );
};

const DetailRow = ({
    label,
    value
}) => {
    return (
        <div className="admin-faq-detail-row">
            <strong>
                {label}
            </strong>
            <span>
                {value !== null &&
                value !== undefined &&
                value !== ''
                    ? value
                    : '-'
                }
            </span>
        </div>
    );
};
export default AdminFaqList;
